'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { ProductCombobox } from '@shared/components/ui/ProductCombobox';
import { Button } from '@shared/components/ui/button';
import { useProductSearch } from '@shared/hooks/useProductSearch';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCartsFilters } from '../../hooks/useCartsFilters';

interface CartsFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

/** Campo de filtro con label */
const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

/**
 * Panel de filtros del módulo Carritos.
 * Tipo de filtro por producto (contiene / no contiene / sin producto / con producto) y selector de producto.
 */
export const CartsFiltersPanel = ({ initialConditions = [], onChange, className }: CartsFiltersPanelProps) => {
  const { t } = useTranslation('cartsModule');
  const { searchProductsForCombobox } = useProductSearch();

  const { filters, handleProductFilterTypeChange, handleProductChange, clearAllFilters } = useCartsFilters({
    initialConditions,
    onChange,
  });

  const productFilterTypeOptions = useMemo(
    () => [
      { label: t('filter.product_contains'), value: 'in' },
      { label: t('filter.product_not_contains'), value: 'not_in' },
      { label: t('filter.product_empty'), value: 'is_null' },
      { label: t('filter.product_any'), value: 'is_not_null' },
    ],
    [t],
  );

  const activeFiltersCount = useMemo(() => {
    if (!filters.productFilterAction) return 0;
    return 1;
  }, [filters]);

  const showProductSelector = filters.productFilterAction === 'in' || filters.productFilterAction === 'not_in';

  return (
    <div className={cn('space-y-5', className)}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FilterField label={t('filter.product_filter_type', 'Filtrar por producto')}>
          <Combobox
            options={productFilterTypeOptions}
            value={filters.productFilterAction}
            setValue={handleProductFilterTypeChange}
            placeholder={t('filter.select_value', 'Seleccionar...')}
            dataTestId="filter-product-type"
          />
        </FilterField>

        {showProductSelector && (
          <FilterField label={t('filter.product', 'Producto')}>
            <ProductCombobox
              value={filters.productId}
              setValue={handleProductChange}
              placeholder={t('search.products_placeholder', 'Buscar productos...')}
              searchFn={searchProductsForCombobox}
              emptyMessage={t('search.no_products_found', 'No se encontraron productos')}
              loadingMessage={t('loading.filtering_products', 'Filtrando productos...')}
              dataTestId="filter-product"
            />
          </FilterField>
        )}
      </div>

      {activeFiltersCount > 0 && (
        <div className="flex justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
            data-testid="carts-clear-filters"
          >
            {t('filter.clear_filters', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};
